
import { useState, useEffect } from 'react';
import { ResellerPlanDetails } from './types';
import { defaultResellerPlan } from './defaultPlans';
import { useToast } from '@/hooks/use-toast';

export const useResellerPlansState = () => {
  const [resellerPlans, setResellerPlans] = useState<ResellerPlanDetails[]>([defaultResellerPlan]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const loadPlans = async () => {
      try {
        console.log("Loading reseller plans...");
        setIsLoading(true);
        
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Load plans from localStorage in demo mode
        const storedPlans = localStorage.getItem('resellerPlans');
        if (storedPlans) {
          console.log("Found stored plans:", JSON.parse(storedPlans));
          setResellerPlans(JSON.parse(storedPlans));
        }
        
        console.log("Plans loaded successfully");
      } catch (error) {
        console.error('Error loading reseller plans:', error);
        toast({
          title: "Error",
          description: "Failed to load reseller plans",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadPlans();
  }, [toast]);

  const validatePlan = (plan: ResellerPlanDetails): ResellerPlanDetails => {
    return {
      ...plan,
      setupFee: Number(plan.setupFee || 0),
      maxInstances: Number(plan.maxInstances || 0),
      maxManagers: Number(plan.maxManagers || 0),
      maxAdmins: Number(plan.maxAdmins || 0),
      maxUsers: Number(plan.maxUsers || 0),
      features: plan.features || [],
    };
  };

  const saveResellerPlans = async (): Promise<boolean> => {
    console.log("Saving reseller plans");
    setIsSaving(true);
    
    try {
      // Validate all plans
      const validatedPlans = resellerPlans.map(validatePlan);
      
      // In a real app, call API - for demo we use localStorage
      localStorage.setItem('resellerPlans', JSON.stringify(validatedPlans));
      console.log("Plans saved successfully to localStorage");
      
      // Update state with validated plans
      setResellerPlans(validatedPlans);
      
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 800));
      
      toast({
        title: "Success",
        description: "Reseller plans have been saved successfully",
      });
      
      return true;
    } catch (error) {
      console.error('Error saving reseller plans:', error);
      toast({
        title: "Error",
        description: "Failed to save reseller plans",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return {
    resellerPlans,
    setResellerPlans,
    isLoading,
    isSaving,
    saveResellerPlans
  };
};
